import { ReactNode, useCallback, useMemo, memo } from 'react'
import {
  Box,
  Flex,
  useDisclosure,
  useColorMode,
} from '@chakra-ui/react'
import { useAuthStore } from '@/store/auth'
import { useNavigate } from 'react-router-dom'
import { Header } from '@/components/layout/Header'
import { Sidebar } from '@/components/layout/Sidebar'
import { useThemeColors } from '@/hooks/useThemeColors'

interface AdminLayoutProps {
  children: ReactNode
  /**
   * When true, the page content area is constrained (minW=0) and scrollable (overflow auto).
   * Use for pages with wide content (e.g. tables) so the sidebar stays visible and content scrolls horizontally.
   * Default false to preserve existing behavior for other pages.
   */
  constrainedContent?: boolean
}

function AdminLayoutComponent({ children, constrainedContent = false }: AdminLayoutProps) {
  // Only extract the specific color values we need to prevent re-renders
  const bgColor = useThemeColors().bg
  const { colorMode } = useColorMode()
  const { isOpen, onOpen, onClose } = useDisclosure()
  // Only subscribe to user and logout, not the entire store
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const navigate = useNavigate()

  const handleLogout = useCallback(async () => {
    // Navigate before clearing auth: otherwise the unauthenticated route tree briefly matches `*`
    // while the URL is still the protected page, and UnauthenticatedRedirect re-saves that URL to sessionStorage.
    navigate('/login', { replace: true })
    await logout()
  }, [logout, navigate])

  // Memoize onClose to prevent Sidebar re-renders - use useCallback to ensure stable reference
  const handleSidebarClose = useCallback(() => {
    onClose()
  }, [onClose])

  // Memoize onOpen to prevent Header re-renders
  const handleSidebarOpen = useCallback(() => {
    onOpen()
  }, [onOpen])

  // Navigation is now driven by user.user_routes inside Sidebar

  return (
    <Box minH="100vh" bg={bgColor}>
      <Flex>
        {/* Desktop Sidebar - colorMode prop so sidebar re-renders when theme is toggled in header */}
        <Sidebar isOpen={isOpen} onClose={handleSidebarClose} colorMode={colorMode} />

        {/* Main Content */}
        <Box
          flex="1"
          minW={constrainedContent ? 0 : undefined}
          bg={bgColor}
          display={constrainedContent ? 'flex' : undefined}
          flexDirection={constrainedContent ? 'column' : undefined}
        >
          {/* Header */}
          <Header onOpen={handleSidebarOpen} user={user} onLogout={handleLogout} />

          {/* Page Content */}
          <Box
            bg={bgColor}
            position="relative"
            {...(constrainedContent
              ? { flex: 1, minH: 0, minW: 0, overflow: 'auto' }
              : { h: 'calc(100vh - 85px)', maxH: 'calc(100vh - 85px)', overflow: 'hidden' })}
          >
            {children}
          </Box>
        </Box>
      </Flex>
    </Box>
  )
}

// Memoize AdminLayout to prevent unnecessary re-renders
export const AdminLayout = memo(AdminLayoutComponent)

